import React, { useEffect, useState, useCallback } from 'react';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Edit,
  Eye,
  FileText,
  GitBranch,
  Landmark,
  RefreshCw,
  User,
  X
} from 'lucide-react';
import api from 'services/api';
import { usePermissions } from 'hooks/usePermissions';
import ModalVersoesConciliacao from './ModalVersoesConciliacao';
import ModalDetalhesConciliacao from './ModalDetalhesConciliacao';

const WEEK_DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
const MONTH_LABEL = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' });

const toDateOnly = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === 'string') {
    if (value.includes('T')) {
      const [year, month, day] = value.split('T')[0].split('-').map(Number);
      if (year && month && day) {
        return new Date(year, month - 1, day);
      }
    }

    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      return new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
    }

    const brMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    if (brMatch) {
      return new Date(Number(brMatch[3]), Number(brMatch[2]) - 1, Number(brMatch[1]));
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const formatDateForApi = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getMonthBounds = (date) => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { start, end };
};

const getCalendarDays = (date) => {
  const { start, end } = getMonthBounds(date);
  const gridStart = new Date(start);
  gridStart.setDate(gridStart.getDate() - gridStart.getDay());

  const gridEnd = new Date(end);
  gridEnd.setDate(gridEnd.getDate() + (6 - gridEnd.getDay()));

  const days = [];
  for (let cursor = new Date(gridStart); cursor <= gridEnd; cursor.setDate(cursor.getDate() + 1)) {
    days.push(new Date(cursor));
  }

  return days;
};

const HistoricoConciliacoes = () => {
  const { hasPermission } = usePermissions();
  const podeEditarConciliacao = hasPermission('administrativo.edit');

  const [conciliacoes, setConciliacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState({
    id_conta_bancaria: '',
    data_inicio: '',
    data_fim: ''
  });
  const [paginacao, setPaginacao] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    last_page: 1
  });
  const [mesVisivel, setMesVisivel] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false));
  const [modalVersoesAberto, setModalVersoesAberto] = useState(false);
  const [versaoSelecionada, setVersaoSelecionada] = useState(null);
  const [modalDetalhesAberto, setModalDetalhesAberto] = useState(false);
  const [conciliacaoIdSelecionada, setConciliacaoIdSelecionada] = useState(null);
  const [diaSelecionado, setDiaSelecionado] = useState(null);
  const [modalDiaAberto, setModalDiaAberto] = useState(false);
  const [_reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const carregarHistorico = useCallback(async () => {
    setLoading(true);
    try {
      const paramsObject = {
        page: '1',
        per_page: isMobile ? paginacao.per_page.toString() : '500'
      };

      if (isMobile) {
        paramsObject.page = paginacao.current_page.toString();
        if (filtros.data_inicio) paramsObject.data_inicio = filtros.data_inicio;
        if (filtros.data_fim) paramsObject.data_fim = filtros.data_fim;
      } else {
        const { start, end } = getMonthBounds(mesVisivel);
        paramsObject.data_inicio = formatDateForApi(start);
        paramsObject.data_fim = formatDateForApi(end);
      }

      if (filtros.id_conta_bancaria) {
        paramsObject.id_conta_bancaria = filtros.id_conta_bancaria;
      }

      const params = new URLSearchParams(paramsObject);
      const response = await api.get(`/n8n/administracao/conciliacao-bancaria/historico?${params}`);

      if (response.data.success) {
        const payload = response.data.data || {};
        setConciliacoes(payload.data || []);
        setPaginacao(prev => ({
          ...prev,
          current_page: payload.current_page || 1,
          per_page: payload.per_page || prev.per_page,
          total: payload.total || 0,
          last_page: payload.last_page || 1
        }));
      }
    } catch (err) {
      console.error('Erro ao carregar histórico:', err);
    } finally {
      setLoading(false);
    }
  }, [filtros, paginacao.current_page, paginacao.per_page, mesVisivel, isMobile]);

  useEffect(() => {
    carregarHistorico();
  }, [carregarHistorico]);

  useEffect(() => {
    const handleCompanyChange = () => {
      setPaginacao(prev => ({ ...prev, current_page: 1 }));
      setReloadToken((prev) => prev + 1);
    };

    window.addEventListener('company-context-changed', handleCompanyChange);
    window.addEventListener('company-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-context-changed', handleCompanyChange);
      window.removeEventListener('company-changed', handleCompanyChange);
    };
  }, []);

  const formatarData = (data) => {
    if (!data) return '-';
    const date = toDateOnly(data);
    if (!date) return data;
    return date.toLocaleDateString('pt-BR');
  };

  const formatarDataCurta = (data) => {
    const date = toDateOnly(data);
    if (!date) return '-';
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
    setPaginacao(prev => ({ ...prev, current_page: 1 }));
  };

  const handlePageChange = (page) => {
    setPaginacao(prev => ({ ...prev, current_page: page }));
  };

  const abrirDetalhes = (conciliacaoId) => {
    setModalDiaAberto(false);
    setDiaSelecionado(null);
    setConciliacaoIdSelecionada(conciliacaoId);
    setModalDetalhesAberto(true);
  };

  const abrirVersoes = (conciliacao) => {
    setModalDiaAberto(false);
    setDiaSelecionado(null);
    const formatarDataParaAPI = (data) => {
      const parsed = toDateOnly(data);
      return parsed ? formatDateForApi(parsed) : null;
    };

    setVersaoSelecionada({
      id_conta_bancaria: conciliacao.id_conta_bancaria,
      data_conciliacao: formatarDataParaAPI(conciliacao.data_conciliacao),
      periodo_inicio: formatarDataParaAPI(conciliacao.periodo_inicio),
      periodo_fim: formatarDataParaAPI(conciliacao.periodo_fim)
    });
    setModalVersoesAberto(true);
  };

  const abrirParaEdicao = (conciliacaoId) => {
    setModalDiaAberto(false);
    setDiaSelecionado(null);
    localStorage.setItem('conciliacao_editar_id', conciliacaoId.toString());
    window.dispatchEvent(new CustomEvent('navigate-to-conciliacao'));
  };

  const abrirConsolidadoDia = (dia) => {
    setDiaSelecionado(dia);
    setModalDiaAberto(true);
  };

  const navegarMes = (incremento) => {
    setMesVisivel(prev => new Date(prev.getFullYear(), prev.getMonth() + incremento, 1));
  };

  const irParaHoje = () => {
    const today = new Date();
    setMesVisivel(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const conciliacoesPorDia = conciliacoes.reduce((acc, conciliacao) => {
    const parsedDate = toDateOnly(conciliacao.data_conciliacao);
    if (!parsedDate) return acc;

    const key = formatDateForApi(parsedDate);
    if (!acc[key]) acc[key] = [];
    acc[key].push(conciliacao);
    return acc;
  }, {});

  const diasCalendario = getCalendarDays(mesVisivel);
  const mesAtual = mesVisivel.getMonth();
  const hoje = toDateOnly(new Date());
  const chaveDiaSelecionado = diaSelecionado ? formatDateForApi(diaSelecionado) : null;
  const itensDiaSelecionado = chaveDiaSelecionado ? (conciliacoesPorDia[chaveDiaSelecionado] || []) : [];

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-[24px] border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_18px_45px_rgba(15,23,42,0.12)]">
        <div className="sticky top-0 z-20 border-b border-[color:var(--border)] bg-[linear-gradient(135deg,color-mix(in_srgb,var(--surface)_88%,var(--accent)_12%),color-mix(in_srgb,var(--surface-muted)_92%,transparent))] px-6 py-5 backdrop-blur-sm md:static">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <p className="text-[color:var(--text-muted)]">
                Visualize todas as conciliações realizadas com versionamento
              </p>
              {!isMobile && (
                <div className="inline-flex items-center gap-2 rounded-full border border-[color:var(--border)] bg-[color:var(--surface)]/80 px-3 py-1 text-xs font-medium text-[color:var(--text-muted)] shadow-sm">
                  <Calendar className="h-3.5 w-3.5 text-[color:var(--accent)]" />
                  Agenda financeira mensal
                </div>
              )}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {!isMobile && (
                <div className="flex items-center gap-2 rounded-[18px] border border-[color:var(--border)] bg-[color:var(--surface)]/88 px-2 py-2 shadow-sm">
                  <button
                    onClick={() => navegarMes(-1)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-transparent text-[color:var(--text-primary)] hover:border-[color:var(--border)] hover:bg-[color:var(--surface-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                    aria-label="Mês anterior"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <div className="min-w-[12rem] px-3 text-center">
                    <p className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--text-muted)]">
                      Período visível
                    </p>
                    <p className="text-base font-semibold capitalize text-[color:var(--text-primary)]">
                      {MONTH_LABEL.format(mesVisivel)}
                    </p>
                  </div>
                  <button
                    onClick={() => navegarMes(1)}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-transparent text-[color:var(--text-primary)] hover:border-[color:var(--border)] hover:bg-[color:var(--surface-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/40"
                    aria-label="Próximo mês"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={irParaHoje}
                    className="ml-1 inline-flex items-center justify-center rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 py-2 text-sm font-medium text-[color:var(--text-primary)] hover:bg-[color:var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                  >
                    Hoje
                  </button>
                </div>
              )}

              <button
                onClick={carregarHistorico}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[color:var(--accent)] px-4 py-2.5 text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-75 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/45"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Atualizar
              </button>
            </div>
          </div>

          <div className={`mt-5 grid grid-cols-1 gap-4 ${isMobile ? 'md:grid-cols-4' : 'lg:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto]'}`}>
            <div>
                <label className="mb-1 block text-sm font-medium text-[color:var(--text-primary)]">
                ID Conta Bancária
                </label>
              <input
                type="number"
                value={filtros.id_conta_bancaria}
                onChange={(e) => handleFiltroChange('id_conta_bancaria', e.target.value)}
                className="w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2.5 text-sm text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
                placeholder="Filtrar por ID"
              />
            </div>

            {isMobile ? (
              <>
                <div>
                  <label className="mb-1 block text-sm font-medium text-[color:var(--text-primary)]">
                    Data Início
                  </label>
                  <input
                    type="date"
                    value={filtros.data_inicio}
                    onChange={(e) => handleFiltroChange('data_inicio', e.target.value)}
                    className="w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2.5 text-sm text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-[color:var(--text-primary)]">
                    Data Fim
                  </label>
                  <input
                    type="date"
                    value={filtros.data_fim}
                    onChange={(e) => handleFiltroChange('data_fim', e.target.value)}
                    className="w-full rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2.5 text-sm text-[color:var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)]"
                  />
                </div>
              </>
            ) : (
              <div className="rounded-[18px] border border-dashed border-[color:var(--border)] bg-[color:var(--surface)]/70 px-4 py-3 text-sm text-[color:var(--text-muted)]">
                O desktop segue o mês visível como fonte de verdade do período. Use a navegação mensal para trocar o intervalo carregado.
              </div>
            )}

            <div className="flex items-end">
              <button
                onClick={() => {
                  setFiltros({ id_conta_bancaria: '', data_inicio: '', data_fim: '' });
                  setPaginacao(prev => ({ ...prev, current_page: 1 }));
                  if (!isMobile) irParaHoje();
                }}
                className="w-full rounded-xl bg-[color:var(--surface-muted)] px-4 py-2.5 text-sm font-medium text-[color:var(--text-primary)] transition hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35 lg:w-auto lg:min-w-[11rem]"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-10 text-center">
            <RefreshCw className="mx-auto mb-4 h-8 w-8 animate-spin text-[color:var(--accent)]" />
            <p className="text-[color:var(--text-muted)]">Carregando histórico...</p>
          </div>
        ) : conciliacoes.length === 0 ? (
          <div className="p-10 text-center">
            <FileText className="mx-auto mb-4 h-12 w-12 text-[color:var(--text-muted)]" />
            <p className="text-[color:var(--text-muted)]">Nenhuma Conciliação encontrada</p>
          </div>
        ) : (
          <>
            <div className="md:hidden space-y-3 p-4">
              {conciliacoes.map((conciliacao) => (
                <div key={conciliacao.id} className="space-y-2 rounded-[20px] border border-[color:var(--border)] bg-[color:var(--surface)] p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                      {formatarData(conciliacao.data_conciliacao)}
                    </div>
                    <div className="text-xs text-[color:var(--text-muted)]">
                      Versão {conciliacao.versao}
                    </div>
                  </div>
                  <div className="text-xs text-[color:var(--text-muted)]">
                    {conciliacao.banco || 'N/A'} • ID {conciliacao.id_conta_bancaria}
                  </div>
                  <div className="text-xs text-[color:var(--text-muted)]">
                    {formatarData(conciliacao.periodo_inicio)} até {formatarData(conciliacao.periodo_fim)}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[color:var(--text-muted)]">
                      Manuais: {conciliacao.conciliacoes_manuais?.length || 0}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => abrirDetalhes(conciliacao.id)}
                        className="inline-flex min-h-[44px] items-center rounded border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-3 py-2 text-sm font-medium text-[color:var(--accent)] transition-colors hover:bg-[color:var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                      >
                        Detalhes
                      </button>
                      {podeEditarConciliacao && (
                        <button
                          onClick={() => abrirParaEdicao(conciliacao.id)}
                          className="inline-flex min-h-[44px] items-center rounded border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-sm font-medium text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                        >
                          Editar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="hidden md:block p-4 lg:p-6">
              <div className="rounded-[26px] border border-[color:var(--border)] bg-[linear-gradient(180deg,color-mix(in_srgb,var(--surface)_92%,var(--accent)_8%),var(--surface))] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
                <div className="mb-4 flex items-center justify-between gap-4 rounded-[20px] border border-[color:var(--border)] bg-[color:var(--surface)]/90 px-4 py-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--text-muted)]">
                      Panorama mensal
                    </p>
                    <p className="text-lg font-semibold capitalize text-[color:var(--text-primary)]">
                      {MONTH_LABEL.format(mesVisivel)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[color:var(--text-muted)]">
                    <span>{conciliacoes.length} conciliações</span>
                    <span className="h-1 w-1 rounded-full bg-[color:var(--text-muted)]/50" />
                    <span>
                      {conciliacoes.reduce((total, item) => total + (item.conciliacoes_manuais?.length || 0), 0)} manuais
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-2">
                  {WEEK_DAYS.map((weekDay) => (
                    <div
                      key={weekDay}
                      className="rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)]/84 px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-[color:var(--text-muted)]"
                    >
                      {weekDay}
                    </div>
                  ))}

                  {diasCalendario.map((dia) => {
                    const key = formatDateForApi(dia);
                    const itensDia = conciliacoesPorDia[key] || [];
                    const isCurrentMonth = dia.getMonth() === mesAtual;
                    const isToday = hoje && hoje.getTime() === dia.getTime();
                    const totalManuaisDia = itensDia.reduce((total, item) => total + (item.conciliacoes_manuais?.length || 0), 0);

                    return (
                      <div
                        key={key}
                        onClick={itensDia.length > 0 ? () => abrirConsolidadoDia(dia) : undefined}
                        onKeyDown={itensDia.length > 0 ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            abrirConsolidadoDia(dia);
                          }
                        } : undefined}
                        role={itensDia.length > 0 ? 'button' : undefined}
                        tabIndex={itensDia.length > 0 ? 0 : undefined}
                        className={`min-h-[16rem] rounded-[24px] border p-3 transition-colors ${
                          isCurrentMonth
                            ? 'border-[color:var(--border)] bg-[color:var(--surface)] shadow-sm'
                            : 'border-[color:var(--border)]/70 bg-[color:var(--surface-muted)]/35 text-[color:var(--text-muted)]'
                        } ${itensDia.length > 0 ? 'cursor-pointer ring-1 ring-[color:var(--accent)]/12 hover:ring-[color:var(--accent)]/35 hover:bg-[color:var(--surface-muted)]/22 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/45' : ''}`}
                      >
                        <div className="mb-3 flex items-start justify-between gap-2">
                          <div className="space-y-1">
                            <div className={`inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-2xl px-3 text-sm font-semibold ${
                              isToday
                                ? 'bg-[color:var(--accent)] text-white'
                                : isCurrentMonth
                                  ? 'bg-[color:var(--surface-muted)] text-[color:var(--text-primary)]'
                                  : 'bg-[color:var(--surface)]/80 text-[color:var(--text-muted)]'
                            }`}>
                              {dia.getDate()}
                            </div>
                            <p className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--text-muted)]">
                              {itensDia.length} registro(s)
                            </p>
                          </div>

                          <div className="text-right">
                            <div className="whitespace-nowrap rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-2 py-1 text-[11px] font-semibold text-[color:var(--accent)]">
                              M: {totalManuaisDia}
                            </div>
                          </div>
                        </div>

                        {itensDia.length === 0 ? (
                          <div className="flex h-[9.25rem] items-center justify-center rounded-[18px] border border-dashed border-[color:var(--border)]/80 bg-[color:var(--surface-muted)]/25 px-4 text-center text-xs text-[color:var(--text-muted)]">
                            Nenhuma conciliação registrada neste dia.
                          </div>
                        ) : (
                          <div className="rounded-[16px] border border-[color:var(--border)] bg-[color:var(--surface-muted)]/38 px-3 py-3">
                            <p className="text-sm font-medium text-[color:var(--text-primary)]">
                              {itensDia.length} {itensDia.length === 1 ? 'conciliação' : 'conciliações'} neste dia
                            </p>
                            <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                              Clique no card para abrir a listagem completa.
                            </p>
                            <div className="mt-3 inline-flex items-center gap-1 rounded-full border border-[color:var(--border)] bg-[color:var(--surface)] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--accent)]">
                              Abrir listagem
                              <ChevronRight className="h-3.5 w-3.5" />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {isMobile && paginacao.last_page > 1 && (
              <div className="flex items-center justify-between border-t border-[color:var(--border)] px-6 py-4">
                <div className="text-sm text-[color:var(--text-muted)]">
                  Mostrando {((paginacao.current_page - 1) * paginacao.per_page) + 1} até{' '}
                  {Math.min(paginacao.current_page * paginacao.per_page, paginacao.total)} de {paginacao.total} resultados
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(paginacao.current_page - 1)}
                    disabled={paginacao.current_page === 1}
                    className="inline-flex min-h-[44px] items-center rounded border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-sm transition-colors hover:bg-[color:var(--surface-muted)] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                  >
                    Anterior
                  </button>
                  <span className="px-3 py-1 text-sm text-[color:var(--text-primary)]">
                    Página {paginacao.current_page} de {paginacao.last_page}
                  </span>
                  <button
                    onClick={() => handlePageChange(paginacao.current_page + 1)}
                    disabled={paginacao.current_page === paginacao.last_page}
                    className="inline-flex min-h-[44px] items-center rounded border border-[color:var(--border)] bg-[color:var(--surface)] px-3 py-2 text-sm transition-colors hover:bg-[color:var(--surface-muted)] disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <ModalVersoesConciliacao
        isOpen={modalVersoesAberto}
        onClose={() => {
          setModalVersoesAberto(false);
          setVersaoSelecionada(null);
        }}
        idContaBancaria={versaoSelecionada?.id_conta_bancaria}
        dataConciliacao={versaoSelecionada?.data_conciliacao}
        periodoInicio={versaoSelecionada?.periodo_inicio}
        periodoFim={versaoSelecionada?.periodo_fim}
      />

      <ModalDetalhesConciliacao
        isOpen={modalDetalhesAberto}
        onClose={() => {
          setModalDetalhesAberto(false);
          setConciliacaoIdSelecionada(null);
        }}
        conciliacaoId={conciliacaoIdSelecionada}
      />

      {modalDiaAberto && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => {
              setModalDiaAberto(false);
              setDiaSelecionado(null);
            }}
          />
          <div className="absolute inset-x-4 bottom-4 top-4 mx-auto flex max-w-5xl flex-col overflow-hidden rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[color:var(--border)] px-5 py-4">
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-[color:var(--text-muted)]">Conciliações do dia</p>
                <h3 className="text-lg font-semibold text-[color:var(--text-primary)]">
                  {diaSelecionado ? diaSelecionado.toLocaleDateString('pt-BR') : '-'}
                </h3>
              </div>
              <button
                onClick={() => {
                  setModalDiaAberto(false);
                  setDiaSelecionado(null);
                }}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-primary)] hover:bg-[color:var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                title="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="overflow-y-auto p-5">
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {itensDiaSelecionado.map((conciliacao) => {
                  const melhorouPendencias =
                    (conciliacao.qtd_pendencias_api_depois + conciliacao.qtd_pendencias_ofx_depois) <
                    (conciliacao.qtd_pendencias_api_antes + conciliacao.qtd_pendencias_ofx_antes);

                  return (
                    <article
                      key={conciliacao.id}
                      className="rounded-[18px] border border-[color:var(--border)] bg-[linear-gradient(180deg,color-mix(in_srgb,var(--surface)_94%,transparent),color-mix(in_srgb,var(--surface-muted)_74%,transparent))] p-3 shadow-[0_8px_18px_rgba(15,23,42,0.08)]"
                    >
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <Landmark className="h-3.5 w-3.5 flex-shrink-0 text-[color:var(--accent)]" />
                            <p className="truncate text-sm font-semibold text-[color:var(--text-primary)]">
                              {conciliacao.banco || `Conta ${conciliacao.id_conta_bancaria}`}
                            </p>
                          </div>
                          <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                            ID {conciliacao.id_conta_bancaria}
                            {conciliacao.conta ? ` • Cc ${conciliacao.conta}` : ''}
                          </p>
                        </div>
                        <span className="rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-2 py-1 text-[11px] font-semibold text-[color:var(--text-primary)]">
                          v{conciliacao.versao}
                        </span>
                      </div>

                      <div className="mb-3 space-y-1 text-xs text-[color:var(--text-muted)]">
                        <p>
                          Período {formatarDataCurta(conciliacao.periodo_inicio)} até {formatarDataCurta(conciliacao.periodo_fim)}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className={`rounded-full px-2 py-1 font-semibold ${
                            melhorouPendencias
                              ? 'bg-[color:var(--surface-muted)] text-[color:var(--status-good)]'
                              : 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]'
                          }`}>
                            API {conciliacao.qtd_pendencias_api_depois}
                          </span>
                          <span className={`rounded-full px-2 py-1 font-semibold ${
                            melhorouPendencias
                              ? 'bg-[color:var(--surface-muted)] text-[color:var(--status-good)]'
                              : 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)]'
                          }`}>
                            OFX {conciliacao.qtd_pendencias_ofx_depois}
                          </span>
                          <span className="rounded-full bg-[color:var(--surface-muted)] px-2 py-1 font-semibold text-[color:var(--accent)]">
                            Conc {conciliacao.qtd_conciliadas_depois}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-1.5 text-xs text-[color:var(--text-muted)]">
                          <span className="whitespace-nowrap rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-2 py-1 text-[color:var(--accent)]">
                            M: {conciliacao.conciliacoes_manuais?.length || 0}
                          </span>
                          {conciliacao.total_versoes > 1 && (
                            <button
                              onClick={() => abrirVersoes(conciliacao)}
                              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-[color:var(--border)] bg-[color:var(--surface-muted)] px-2 py-1 font-semibold text-[color:var(--accent)] transition-colors hover:bg-[color:var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                              title={`${conciliacao.total_versoes} versões disponíveis`}
                            >
                              <GitBranch className="h-3 w-3" />
                              {conciliacao.total_versoes}
                            </button>
                          )}
                        </div>

                        <div className="ml-auto flex items-center gap-2">
                          <button
                            onClick={() => abrirDetalhes(conciliacao.id)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--accent)] transition-colors hover:bg-[color:var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                            title="Ver detalhes"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          {podeEditarConciliacao && (
                            <button
                              onClick={() => abrirParaEdicao(conciliacao.id)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--surface-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]/35"
                              title="Editar Conciliação"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {conciliacao.criado_por && (
                        <div className="mt-3 flex items-center gap-2 border-t border-[color:var(--border)] pt-3 text-xs text-[color:var(--text-muted)]">
                          <User className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">
                            {conciliacao.criado_por.full_name || conciliacao.criado_por.corporate_email}
                          </span>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoricoConciliacoes;
